import React from "react";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { CheckCircle2, Clock3, RotateCcw } from "lucide-react";

const accentColor = "#16c784";

function clampPercent(value) {
	if (!Number.isFinite(value)) {
		return 0;
	}

	return Math.min(100, Math.max(0, value));
}

function StatPill({ icon: Icon, label, value, color, backgroundColor }) {
	return (
		<Paper
			variant="outlined"
			sx={{
				alignItems: "center",
				borderColor: "#e5e7eb",
				borderRadius: "10px",
				boxShadow: "none",
				display: "flex",
				height: { xs: 42, sm: 48 },
				minWidth: { xs: 0, sm: 152 },
				px: { xs: 1, sm: 2 },
				py: 0,
				width: { xs: "auto", sm: "auto" },
			}}
		>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				spacing={{ xs: 1, sm: 2 }}
				sx={{ width: "100%" }}
			>
				<Stack direction="row" alignItems="center" spacing={{ xs: 0, sm: 1 }} sx={{ height: 30, minWidth: 0 }}>
					<Box
						component="span"
						sx={{
							alignItems: "center",
							display: "inline-flex",
							height: 30,
							justifyContent: "center",
							lineHeight: 0,
							width: { xs: 20, sm: 18 },
						}}
					>
						<Icon size={17} color={color} strokeWidth={2.3} />
					</Box>
					<Typography
						variant="body2"
						component="span"
						sx={{
							alignItems: "center",
							color: "#111827",
							display: { xs: "none", sm: "flex" },
							fontWeight: 500,
							height: 30,
							lineHeight: 1,
							whiteSpace: "nowrap",
						}}
					>
						{label}
					</Typography>
				</Stack>
				<Box
					component="span"
					sx={{
						alignItems: "center",
						backgroundColor,
						borderRadius: "8px",
						color,
						display: "inline-flex",
						fontSize: 18,
						fontWeight: 700,
						height: { xs: 28, sm: 30 },
						justifyContent: "center",
						lineHeight: 1,
						minWidth: { xs: 32, sm: 42 },
						px: 1,
					}}
				>
					{value}
				</Box>
			</Stack>
		</Paper>
	);
}

export default function FlashcardsHeader({ data }) {
	const session = data?.session || {};
	const flashcard = data?.flashcard || {};
	const isInfinite = Boolean(session.is_infinite);

	const totalCards = Number(session.total_cards) || 0;
	const currentCard = Math.min(
		totalCards || 1,
		Number(flashcard.position) || Number(session.reviewed_cards) + 1 || 1
	);
	const progress = clampPercent(totalCards > 0 ? (currentCard / totalCards) * 100 : 0);
	const progressLabel = `${Math.round(progress)}%`;

	return (
		<Paper
			variant="outlined"
			sx={{
				borderColor: "#dfe5ee",
				borderRadius: "10px",
				boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
				p: { xs: 2, md: 2.5 },
				width: "100%",
			}}
		>
			<Stack
				direction={{ xs: "column", lg: "row" }}
				alignItems={{ xs: "stretch", lg: "center" }}
				justifyContent="space-between"
				spacing={{ xs: 2, lg: 4 }}
			>
				<Stack
					direction={{ xs: "column", md: "row" }}
					alignItems={{ xs: "stretch", md: "center" }}
					spacing={{ xs: 1.5, md: 3 }}
					sx={{ flex: 1, minHeight: { md: 48 }, minWidth: 0 }}
				>
					<Typography
						variant="subtitle1"
						sx={{
							alignItems: "center",
							color: "#111827",
							display: "flex",
							fontWeight: 600,
							height: { xs: "auto", md: 48 },
							lineHeight: 1,
							minWidth: { md: 122 },
							whiteSpace: "nowrap",
						}}
					>
						Tarjeta {currentCard}{!isInfinite && ` de ${totalCards}`}
					</Typography>
					<Box sx={{ alignItems: "center", display: "flex", flex: 1, gap: 2, minHeight: { md: 48 }, minWidth: 0 }}>
						<LinearProgress
							variant="determinate"
							value={progress}
							sx={{
								backgroundColor: "#f1f5f9",
								borderRadius: 999,
								flex: 1,
								height: 10,
								minWidth: { xs: 120, md: 260 },
								"& .MuiLinearProgress-bar": {
									backgroundColor: accentColor,
									borderRadius: 999,
								},
							}}
						/>
						<Typography
							variant="subtitle1"
							sx={{ color: accentColor, fontWeight: 800, minWidth: 48 }}
						>
							{progressLabel}
						</Typography>
					</Box>
				</Stack>

				<Stack
					direction="row"
					alignItems="stretch"
					justifyContent={{ xs: "flex-start", lg: "flex-end" }}
					spacing={{ xs: 1, sm: 2 }}
					sx={{ flexWrap: "nowrap", overflowX: "auto" }}
				>
					<StatPill
						icon={RotateCcw}
						label="Revisadas"
						value={Number(session.reviewed_cards) || 0}
						color="#2563eb"
						backgroundColor="#dbeafe"
					/>
					<StatPill
						icon={CheckCircle2}
						label="Correctas"
						value={Number(session.correct_cards) || 0}
						color="#047857"
						backgroundColor="#bbf7d0"
					/>
					{!isInfinite && (
						<StatPill
							icon={Clock3}
							label="Pendientes"
							value={Number(session.pending_cards) || 0}
							color="#f97316"
							backgroundColor="#ffedd5"
						/>
					)}
				</Stack>
			</Stack>
		</Paper>
	);
}
